// src/pages/quote/QuoteEdit.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FileText,
  CalendarDays,
  Building2,
  DollarSign,
  Percent,
  Plus,
  Minus,
  Copy as CopyIcon,
  ArrowUp,
  ArrowDown,
  Info,
  MapPin,
  ArrowLeft,
  FileDown,
} from "lucide-react";

import api from "../../api/axios";
import { formatMoney, safeCurrency } from "../../utils/money";

/* ----------------------------- local UI primitives ----------------------------- */
const INPUT_BASE =
  "block w-full h-11 rounded-xl border border-slate-300 bg-white px-3.5 text-[0.95rem] leading-6 text-slate-900 placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500";
const LABEL_BASE = "text-[13px] font-medium text-slate-700";
const HINT_BASE = "text-[12px] text-slate-500";

function LabeledInput({
  id,
  name,
  label,
  labelRight = null,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
  disabled = false,
  leftAdornment = null,
  rightAdornment = null,
  inputRef,
  onKeyDown,
  onFocus,
  onClick,
  autoComplete = "off",
}) {
  const hasLeft = !!leftAdornment;
  const hasRight = !!rightAdornment;
  return (
    <div className="space-y-1.5">
      {label ? (
        <div className="flex items-center justify-between">
          <label htmlFor={id || name} className={LABEL_BASE}>
            {label} {required ? <span className="text-rose-500">*</span> : null}
          </label>
          {labelRight ? <div className="ml-3 text-[12px]">{labelRight}</div> : null}
        </div>
      ) : null}

      <div className="relative">
        {hasLeft ? (
          <div className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-slate-400">
            {leftAdornment}
          </div>
        ) : null}
        <input
          ref={inputRef}
          id={id || name}
          name={name}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onClick={onClick}
          placeholder={placeholder}
          type={type}
          disabled={disabled}
          autoComplete={autoComplete}
          className={[INPUT_BASE, hasLeft ? "pl-10" : "", hasRight ? "pr-10" : ""]
            .filter(Boolean)
            .join(" ")}
          required={required}
        />
        {hasRight ? (
          <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-slate-400">
            {rightAdornment}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LabeledCheckbox({ label, checked, onChange, disabled = false }) {
  return (
    <label
      className={`inline-flex items-center gap-2 text-[13px] ${
        disabled ? "text-slate-400" : "text-slate-700"
      }`}
    >
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
      {label}
    </label>
  );
}

function Section({ icon: Icon, title, hint, children }) {
  return (
    <div className="border rounded-2xl p-5 space-y-4 shadow-sm bg-white">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 text-slate-700">
          <Icon size={18} />
        </div>
        <div>
          <div className="text-slate-800 font-semibold">{title}</div>
          {hint ? <div className={HINT_BASE}>{hint}</div> : null}
        </div>
      </div>
      {children}
    </div>
  );
}

/* --------------------------------- math (server-aligned) --------------------------------- */
const round2 = (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;
const clamp = (val, min, max) => Math.min(Math.max(Number(val || 0), min), max);

function calcLine(li) {
  const qty = Math.max(0, Number(li.quantity || 0));
  const rate = Math.max(0, Number(li.rate || 0));
  const taxPct = clamp(li.taxPercent, 0, 100);
  const dPct = clamp(li.discountPercent, 0, 100);
  const dAmt = Math.max(0, Number(li.discountAmount || 0));
  const taxIncl = !!li.isTaxInclusive;

  let unitBase = rate;
  if (taxIncl && taxPct > 0) unitBase = rate / (1 + taxPct / 100);

  const afterDiscount =
    dAmt > 0 ? Math.max(0, unitBase - dAmt) : unitBase * (1 - (dPct > 0 ? dPct / 100 : 0));

  const subtotalBase = round2(qty * unitBase); // before any discount
  const subtotalAfterDiscount = round2(qty * afterDiscount); // after discount, before tax
  const lineDiscount = round2(Math.max(0, subtotalBase - subtotalAfterDiscount));

  const taxAmount = round2(subtotalAfterDiscount * (taxPct > 0 ? taxPct / 100 : 0));
  const total = round2(subtotalAfterDiscount + taxAmount);
  return { subtotalBase, subtotal: subtotalAfterDiscount, taxAmount, total, lineDiscount };
}

function computeTotals(lineItems) {
  let subtotalBase = 0;
  let taxTotal = 0;
  let lineDiscountTotal = 0;
  let totalAmount = 0;

  for (const li of lineItems) {
    const c = calcLine(li);
    subtotalBase += c.subtotalBase;
    taxTotal += c.taxAmount;
    lineDiscountTotal += c.lineDiscount || 0;
    totalAmount += c.total;
  }

  return {
    subtotalBase: round2(subtotalBase),
    taxTotal: round2(taxTotal),
    totalAmount: round2(totalAmount),
    lineDiscountTotal: round2(lineDiscountTotal),
  };
}

/* --------- helper: normalize discount fields from server for edit UI --------- */
function normalizeLineItemFromServer(li) {
  const quantity = Number(li.quantity || 0);
  const rate = Number(li.rate ?? li.unitPrice ?? 0);
  const taxPercent = Number(li.taxPercent || 0);
  const rawPct = Number(li.discountPercent || 0);
  const rawAmt = Number(li.discountAmount || 0);

  let discountPercent = rawPct;
  let discountAmount = rawAmt;

  // If both percent and amount are present, decide which one is "real"
  if (rawPct > 0 && rawAmt > 0 && rate > 0) {
    const qtySafe = quantity > 0 ? quantity : 1;
    const perUnitFromPct = round2((rate * rawPct) / 100);
    const perLineFromPct = round2((rate * qtySafe * rawPct) / 100);
    const eps = 0.01;

    const matchesDerived =
      Math.abs(rawAmt - perUnitFromPct) < eps ||
      Math.abs(rawAmt - perLineFromPct) < eps;

    if (matchesDerived) {
      // Treat percent as primary UI input → clear amount
      discountAmount = 0;
    } else {
      // Treat amount as primary UI input → clear percent
      discountPercent = 0;
    }
  }

  return {
    _id: li._id,
    description: li.description || li.name || "",
    quantity,
    rate,
    taxPercent,
    discountPercent,
    discountAmount,
    isTaxInclusive: !!li.isTaxInclusive,
    showDiscount: discountAmount > 0 || discountPercent > 0,
  };
}

/* --------------------------------- helpers --------------------------------- */
const money = (n, c = "USD") => formatMoney(n, safeCurrency(c));

const formatISODateInTz = (dateLike, tz) => {
  try {
    const d = dateLike ? new Date(dateLike) : new Date();
    if (!tz) return d.toISOString().slice(0, 10);
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(d);
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const day = parts.find((p) => p.type === "day")?.value;
    if (y && m && day) return `${y}-${m}-${day}`;
    return d.toISOString().slice(0, 10);
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};

const todayISO = (tz) => formatISODateInTz(new Date(), tz);
const emptyAddress = {
  street: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  raw: "",
};

/* --------------------------------- Page --------------------------------- */

export default function QuoteEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  // basics
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // company defaults
  const [companyCurrency, setCompanyCurrency] = useState("");
  const [companyTimezone, setCompanyTimezone] = useState("");
  const [currencyLockedFromQuote, setCurrencyLockedFromQuote] = useState(false);

  // quote fields
  const [quoteNumber, setQuoteNumber] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSnapshot, setClientSnapshot] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [issueDate, setIssueDate] = useState(todayISO());
  const [expiryDate, setExpiryDate] = useState(todayISO());
  const [notes, setNotes] = useState("");
  const [lifecycleStatus, setLifecycleStatus] = useState("");

  // line items
  const [lineItems, setLineItems] = useState([]);
  const [defaultTaxPct, setDefaultTaxPct] = useState(0);
  const [defaultTaxInclusive, setDefaultTaxInclusive] = useState(false);

  // addresses
  const [billingAddress, setBillingAddress] = useState({ ...emptyAddress });
  const [shippingAddress, setShippingAddress] = useState({ ...emptyAddress });
  const [useBillingAsShipping, setUseBillingAsShipping] = useState(false);

  // anchor (parity with other pages)
  const inputAnchorRef = useRef(null);

  const invalidExpiry =
    issueDate && expiryDate ? new Date(expiryDate) < new Date(issueDate) : false;
  const isEditLocked = ["accepted", "canceled"].includes(
    String(lifecycleStatus || "").toLowerCase()
  );

  const canSave =
    !isEditLocked &&
    clientId &&
    lineItems.length > 0 &&
    lineItems.some(
      (li) =>
        Number(li.quantity) > 0 &&
        (Number(li.rate) > 0 ||
          Number(li.discountAmount) > 0 ||
          Number(li.discountPercent) > 0)
    ) &&
    !invalidExpiry;

  const totals = useMemo(() => computeTotals(lineItems), [lineItems]);

  /* ---------------------------- load company prefs ---------------------------- */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/company/profile");
        const data = res?.data || {};
        if (!alive) return;
        const cur =
          (data.defaultCurrency || data.currency || data.billingCurrency || "").toUpperCase();
        const tz = data.timezone || "";
        if (cur) setCompanyCurrency(cur);
        if (tz) setCompanyTimezone(tz);
        if (!currencyLockedFromQuote && cur) {
          setCurrency((prev) => {
            if (prev && prev !== "USD") return prev;
            return cur;
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to load company profile for currency/timezone", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [currencyLockedFromQuote]);

  /* ---------------------------- load existing quote ---------------------------- */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/quotes/${id}`);
      const q = res?.data?.data || res?.data;

      if (!q) throw new Error("Quote not found");

      setQuoteNumber(q.quoteNumber || "");
      setClientId(q.clientId || q.client?._id || "");
      setClientSnapshot(q.clientSnapshot || null);
      const currencyFromQuote = (q.currency || "").toUpperCase();
      if (currencyFromQuote) {
        setCurrency(currencyFromQuote);
        setCurrencyLockedFromQuote(true);
      } else if (companyCurrency) {
        setCurrency(companyCurrency);
      } else {
        setCurrency("USD");
      }
      const fallbackIssue = todayISO(companyTimezone || undefined);
      setIssueDate(q.issueDate ? q.issueDate.slice(0, 10) : fallbackIssue);
      setExpiryDate(
        q.expiryDate
          ? q.expiryDate.slice(0, 10)
          : q.issueDate
          ? q.issueDate.slice(0, 10)
          : fallbackIssue
      );
      setNotes(q.notes || "");
      setLifecycleStatus(q.lifecycleStatus || "");

      // addresses
      const bill = q.billingAddress || q.clientSnapshot?.address || {};
      const ship = q.shippingAddress || {};
      setBillingAddress({
        street: bill.street || "",
        city: bill.city || "",
        state: bill.state || "",
        zip: bill.zip || bill.postalCode || "",
        country: bill.country || "",
        raw: bill.raw || "",
      });
      setShippingAddress({
        street: ship.street || "",
        city: ship.city || "",
        state: ship.state || "",
        zip: ship.zip || ship.postalCode || "",
        country: ship.country || "",
        raw: ship.raw || "",
      });
      setUseBillingAsShipping(
        !!bill &&
          !!ship &&
          ["street", "city", "state", "zip", "country", "raw"].every(
            (k) => (bill?.[k] || "") === (ship?.[k] || "")
          )
      );

      // line items (normalize discount fields)
      const lis = Array.isArray(q.lineItems) ? q.lineItems : [];
      const mapped = lis.map(normalizeLineItemFromServer);
      setLineItems(mapped);

      // sensible defaults from first line
      if (mapped.length) {
        setDefaultTaxPct(Number(mapped[0].taxPercent || 0));
        setDefaultTaxInclusive(!!mapped[0].isTaxInclusive);
      } else {
        setDefaultTaxPct(0);
        setDefaultTaxInclusive(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to load quote");
    } finally {
      setLoading(false);
    }
  }, [id, companyCurrency, companyTimezone]);

  useEffect(() => {
    load();
  }, [load]);

  /* ------------------------------- handlers ------------------------------- */
  const patchLine = (i, field, val) =>
    setLineItems((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: val };
      return next;
    });

  const setLineDiscountPercent = (i, value) => {
    const v = Number(value || 0);
    setLineItems((prev) => {
      const next = [...prev];
      const li = { ...next[i] };
      li.discountPercent = v;
      if (v > 0) {
        li.discountAmount = 0;
      }
      next[i] = li;
      return next;
    });
  };

  const setLineDiscountAmount = (i, value) => {
    const v = Number(value || 0);
    setLineItems((prev) => {
      const next = [...prev];
      const li = { ...next[i] };
      li.discountAmount = v;
      if (v > 0) {
        li.discountPercent = 0;
      }
      next[i] = li;
      return next;
    });
  };

  const addLine = () =>
    setLineItems((prev) => [
      ...prev,
      {
        description: "",
        quantity: 1,
        rate: 0,
        taxPercent: defaultTaxPct,
        discountPercent: 0,
        discountAmount: 0,
        isTaxInclusive: defaultTaxInclusive,
        showDiscount: false,
      },
    ]);

  const removeLine = (i) => setLineItems((prev) => prev.filter((_, idx) => idx !== i));

  const duplicateLine = (i) =>
    setLineItems((prev) => {
      const copy = { ...prev[i] };
      delete copy._id;
      return [...prev.slice(0, i + 1), copy, ...prev.slice(i + 1)];
    });

  const moveLine = (i, dir) =>
    setLineItems((prev) => {
      const next = [...prev];
      const ni = i + dir;
      if (ni < 0 || ni >= next.length) return prev;
      [next[i], next[ni]] = [next[ni], next[i]];
      return next;
    });

  const applyDefaultsToAll = () => {
    setLineItems((prev) =>
      prev.map((li) => ({
        ...li,
        taxPercent: defaultTaxPct,
        isTaxInclusive: defaultTaxInclusive,
      }))
    );
    toast.success("Applied defaults to all lines");
  };

  const onBillingChange = (name, value) =>
    setBillingAddress((prev) => ({ ...prev, [name]: value }));
  const onShippingChange = (name, value) =>
    setShippingAddress((prev) => ({ ...prev, [name]: value }));

  const save = async ({ closeAfter = false } = {}) => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        clientId,
        issueDate: issueDate || undefined,
        expiryDate: expiryDate || undefined,
        currency: (currency || "USD").toUpperCase(),
        notes,
        lineItems: lineItems.map((li) => ({
          _id: li._id,
          description: li.description || "",
          quantity: Number(li.quantity || 0),
          rate: Number(li.rate || 0),
          unitPrice: Number(li.rate || 0),
          taxPercent: clamp(li.taxPercent, 0, 100),
          discountPercent: clamp(li.discountPercent, 0, 100),
          discountAmount: Number(li.discountAmount || 0),
          isTaxInclusive: !!li.isTaxInclusive,
        })),
        billingAddress,
        shippingAddress: useBillingAsShipping ? { ...billingAddress } : shippingAddress,
        discountTotal: 0, // quote-level discount removed
      };

      await api.put(`/quotes/${id}`, payload);
      toast.success("Quote updated");

      if (closeAfter) navigate(`/quotes/${id}`);
      else load();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to update quote";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // Download-only PDF (server-rendered): no preview/new tab
  const onPdf = async () => {
    try {
      const fileName = `${quoteNumber || id || "quote"}.pdf`.replace(/[^\w.-]+/g, "_");
      const resp = await api.get(`/quotes/${id}/pdf`, {
        responseType: "blob",
        params: {
          download: 1,
          v: Date.now(),
        },
      });

      const blob = resp?.data;
      const ct = String(resp?.headers?.["content-type"] || "").toLowerCase();

      if (ct.includes("application/json")) {
        const text = await blob.text?.();
        try {
          const json = JSON.parse(text || "{}");
          throw new Error(json?.error || "Server error while generating PDF");
        } catch {
          throw new Error(text || "Server error while generating PDF");
        }
      }

      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error("Empty PDF response");
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error(e?.message || "Unable to download PDF");
    }
  };

  // aggregated discount for display (line-level only)
  const aggregatedDiscountDisplay = totals.lineDiscountTotal || 0;

  /* ------------------------------ UI ------------------------------ */
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 sm:px-6 lg:px-8 pt-6">
        <div className="rounded-2xl border border-slate-200 shadow-sm h-40 bg-white animate-pulse" />
        <div className="h-6" />
        <div className="rounded-2xl border border-slate-200 shadow-sm h-[60vh] bg-white animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* normalize native date widget differences */}
      <style>{`
        input[type="date"]::-webkit-inner-spin-button{display:none;}
        input[type="date"]::-webkit-clear-button{display:none;}
        input[type="date"]::-webkit-calendar-picker-indicator{opacity:0;}
      `}</style>

      {/* Header */}
      <div className="px-4 sm:px-6 lg:px-8 pt-6">
        <div className="rounded-2xl border border-slate-200 shadow-sm bg-white">
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <button
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition"
                  onClick={() => navigate(-1)}
                >
                  <ArrowLeft className="inline -mt-1 mr-1" size={16} /> Back
                </button>
                <div className="w-10 h-10 bg-slate-900 text-white rounded-xl flex items-center justify-center">
                  <FileText size={20} />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                    Edit Quote {quoteNumber ? `• ${quoteNumber}` : ""}
                  </h1>
                  <p className="text-slate-600 text-sm">
                    Update details and line items, then save.
                    {isEditLocked ? (
                      <span className="ml-2 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[12px] text-amber-700">
                        Locked ({String(lifecycleStatus).toUpperCase()})
                      </span>
                    ) : null}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => save({ closeAfter: false })}
                  disabled={!canSave || saving}
                  className={`px-4 py-2 rounded-xl font-semibold shadow-sm border border-slate-200 text-slate-700 hover:bg-slate-50 ${
                    !canSave || saving ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => save({ closeAfter: true })}
                  disabled={!canSave || saving}
                  className={`px-4 py-2 rounded-xl font-semibold shadow-sm bg-slate-900 text-white hover:bg-black/90 ${
                    !canSave || saving ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  Save & Close
                </button>
                <button
                  type="button"
                  onClick={onPdf}
                  className="px-4 py-2 rounded-xl font-semibold shadow-sm border border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  <FileDown size={16} className="inline -mt-0.5 mr-2" />
                  PDF
                </button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-slate-500 text-xs flex items-center gap-1">
                  <DollarSign size={14} /> Subtotal
                </div>
                <div className="text-lg font-bold text-slate-900">
                  {money(totals.subtotalBase, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-slate-500 text-xs flex items-center gap-1">
                  <Percent size={14} /> Tax
                </div>
                <div className="text-lg font-bold text-slate-900">
                  {money(totals.taxTotal, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-slate-500 text-xs">Discount</div>
                <div className="text-lg font-bold text-slate-900">
                  -{money(aggregatedDiscountDisplay, currency)}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-slate-500 text-xs">Total</div>
                <div className="text-lg font-bold text-slate-900">
                  {money(totals.totalAmount, currency)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client & Currency */}
          <Section
            icon={Building2}
            title="Client & Currency"
            hint="Client from original quote; currency editable"
          >
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px] gap-4 items-start">
              <div>
                <label className={LABEL_BASE}>Client</label>
                <div className="mt-1 h-11 px-3.5 flex items-center rounded-xl border bg-slate-50 border-slate-200 text-[0.95rem] text-slate-700">
                  {clientSnapshot?.name || "—"}
                  {clientSnapshot?.email ? (
                    <span className="ml-2 text-slate-500 text-[12px]">
                      • {clientSnapshot.email}
                    </span>
                  ) : null}
                </div>
              </div>
              <LabeledInput
                name="currency"
                label="Currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                placeholder="USD"
                disabled={isEditLocked}
              />
            </div>
          </Section>

          {/* Dates */}
          <Section
            icon={CalendarDays}
            title="Dates & Validity"
            hint="Change issue and expiry dates"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <LabeledInput
                name="issueDate"
                label="Issue Date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                type="date"
                rightAdornment={<CalendarDays size={16} />}
                onClick={(e) => e.target.showPicker?.()}
                disabled={isEditLocked}
              />
              <LabeledInput
                name="expiryDate"
                label="Expiry Date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                type="date"
                rightAdornment={<CalendarDays size={16} />}
                onClick={(e) => e.target.showPicker?.()}
                disabled={isEditLocked}
              />
            </div>
            {invalidExpiry ? (
              <div className="text-[12px] text-rose-600 mt-1">
                Expiry must be on or after Issue date.
              </div>
            ) : null}
          </Section>

          {/* Addresses */}
          <Section icon={MapPin} title="Addresses" hint="Edit billing/shipping as needed">
            <div className="flex flex-wrap items-center gap-4 mb-3">
              <LabeledCheckbox
                label="Shipping same as billing"
                checked={useBillingAsShipping}
                onChange={(e) => setUseBillingAsShipping(e.target.checked)}
                disabled={isEditLocked}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Billing */}
              <div className="space-y-3">
                <div className="text-sm font-semibold text-slate-700">Billing Address</div>
                <LabeledInput
                  label="Street"
                  value={billingAddress.street}
                  onChange={(e) => onBillingChange("street", e.target.value)}
                  disabled={isEditLocked}
                />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <LabeledInput
                    label="City"
                    value={billingAddress.city}
                    onChange={(e) => onBillingChange("city", e.target.value)}
                    disabled={isEditLocked}
                  />
                  <LabeledInput
                    label="State/Province"
                    value={billingAddress.state}
                    onChange={(e) => onBillingChange("state", e.target.value)}
                    disabled={isEditLocked}
                  />
                  <LabeledInput
                    label="ZIP/Postal"
                    value={billingAddress.zip}
                    onChange={(e) => onBillingChange("zip", e.target.value)}
                    disabled={isEditLocked}
                  />
                </div>
                <LabeledInput
                  label="Country"
                  value={billingAddress.country}
                  onChange={(e) => onBillingChange("country", e.target.value)}
                  disabled={isEditLocked}
                />
                <label className={LABEL_BASE}>Raw (optional)</label>
                <textarea
                  rows={3}
                  className={INPUT_BASE + " h-auto"}
                  value={billingAddress.raw}
                  onChange={(e) => onBillingChange("raw", e.target.value)}
                  placeholder="Street, City, State, ZIP, Country"
                  disabled={isEditLocked}
                />
              </div>

              {/* Shipping */}
              <div className="space-y-3">
                <div className="text-sm font-semibold text-slate-700">Shipping Address</div>
                <LabeledInput
                  label="Street"
                  value={useBillingAsShipping ? billingAddress.street : shippingAddress.street}
                  onChange={(e) => onShippingChange("street", e.target.value)}
                  disabled={isEditLocked || useBillingAsShipping}
                />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <LabeledInput
                    label="City"
                    value={useBillingAsShipping ? billingAddress.city : shippingAddress.city}
                    onChange={(e) => onShippingChange("city", e.target.value)}
                    disabled={isEditLocked || useBillingAsShipping}
                  />
                  <LabeledInput
                    label="State/Province"
                    value={useBillingAsShipping ? billingAddress.state : shippingAddress.state}
                    onChange={(e) => onShippingChange("state", e.target.value)}
                    disabled={isEditLocked || useBillingAsShipping}
                  />
                  <LabeledInput
                    label="ZIP/Postal"
                    value={useBillingAsShipping ? billingAddress.zip : shippingAddress.zip}
                    onChange={(e) => onShippingChange("zip", e.target.value)}
                    disabled={isEditLocked || useBillingAsShipping}
                  />
                </div>
                <LabeledInput
                  label="Country"
                  value={useBillingAsShipping ? billingAddress.country : shippingAddress.country}
                  onChange={(e) => onShippingChange("country", e.target.value)}
                  disabled={isEditLocked || useBillingAsShipping}
                />
                <label className={LABEL_BASE}>Raw (optional)</label>
                <textarea
                  rows={3}
                  className={INPUT_BASE + " h-auto"}
                  value={useBillingAsShipping ? billingAddress.raw : shippingAddress.raw}
                  onChange={(e) => onShippingChange("raw", e.target.value)}
                  placeholder="Street, City, State, ZIP, Country"
                  disabled={isEditLocked || useBillingAsShipping}
                />
              </div>
            </div>
          </Section>

          {/* Line Items */}
          <Section
            icon={DollarSign}
            title="Items & Services"
            hint="Discount amount overrides percent; 'Tax inclusive' backs out tax from rate."
          >
            {/* Defaults Toolbar */}
            <div className="flex flex-wrap gap-3 items-end">
              <div className="w-28">
                <LabeledInput
                  name="defaultTax"
                  label="Default Tax %"
                  type="number"
                  value={defaultTaxPct}
                  onChange={(e) => setDefaultTaxPct(Number(e.target.value || 0))}
                  disabled={isEditLocked}
                />
              </div>
              <div className="pt-6">
                <LabeledCheckbox
                  label="Tax inclusive"
                  checked={defaultTaxInclusive}
                  onChange={(e) => setDefaultTaxInclusive(e.target.checked)}
                  disabled={isEditLocked}
                />
              </div>
              <button
                type="button"
                className={`ml-auto px-3.5 py-2.5 rounded-xl border text-sm ${
                  isEditLocked ? "opacity-60 cursor-not-allowed" : "hover:bg-slate-50"
                }`}
                onClick={applyDefaultsToAll}
                disabled={isEditLocked}
              >
                Apply defaults to all
              </button>
            </div>

            {/* Cards */}
            <div className="space-y-4 mt-3">
              {lineItems.map((li, i) => {
                const c = calcLine(li);
                const hasDiscount =
                  Number(li.discountAmount) > 0 || Number(li.discountPercent) > 0;

                return (
                  <div
                    key={i}
                    className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-semibold text-slate-700">Item {i + 1}</div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className={`p-2 rounded-xl border ${
                            isEditLocked ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-50"
                          }`}
                          title="Move up"
                          onClick={() => !isEditLocked && moveLine(i, -1)}
                          disabled={isEditLocked}
                        >
                          <ArrowUp size={16} />
                        </button>
                        <button
                          type="button"
                          className={`p-2 rounded-xl border ${
                            isEditLocked ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-50"
                          }`}
                          title="Move down"
                          onClick={() => !isEditLocked && moveLine(i, 1)}
                          disabled={isEditLocked}
                        >
                          <ArrowDown size={16} />
                        </button>
                        <button
                          type="button"
                          className={`p-2 rounded-xl border ${
                            isEditLocked ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-50"
                          }`}
                          title="Duplicate"
                          onClick={() => !isEditLocked && duplicateLine(i)}
                          disabled={isEditLocked}
                        >
                          <CopyIcon size={16} />
                        </button>
                        <button
                          type="button"
                          className={`p-2 rounded-xl border text-rose-600 ${
                            isEditLocked ? "opacity-50 cursor-not-allowed" : "hover:bg-rose-50"
                          }`}
                          title="Remove"
                          onClick={() => !isEditLocked && removeLine(i)}
                          disabled={isEditLocked}
                        >
                          <Minus size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                      <LabeledInput
                        label="Qty"
                        type="number"
                        value={li.quantity}
                        onChange={(e) => patchLine(i, "quantity", Number(e.target.value || 0))}
                        disabled={isEditLocked}
                      />
                      <LabeledInput
                        label="Rate"
                        type="number"
                        value={li.rate}
                        onChange={(e) => patchLine(i, "rate", Number(e.target.value || 0))}
                        rightAdornment={<DollarSign size={14} />}
                        disabled={isEditLocked}
                      />

                      {/* Discount block */}
                      <div className="col-span-2">
                        {li.showDiscount ? (
                          <div className="grid grid-cols-2 gap-3">
                            <LabeledInput
                              label="Disc %"
                              type="number"
                              value={li.discountPercent}
                              onChange={(e) =>
                                setLineDiscountPercent(
                                  i,
                                  e.target.value === "" ? "" : Number(e.target.value)
                                )
                              }
                              rightAdornment={<Percent size={14} />}
                              disabled={isEditLocked}
                            />
                            <LabeledInput
                              label="Disc $"
                              type="number"
                              value={li.discountAmount}
                              onChange={(e) =>
                                setLineDiscountAmount(
                                  i,
                                  e.target.value === "" ? "" : Number(e.target.value)
                                )
                              }
                              rightAdornment={<DollarSign size={14} />}
                              disabled={isEditLocked}
                            />
                            <div className="col-span-2">
                              <button
                                type="button"
                                className={`text-[12px] underline ${
                                  isEditLocked
                                    ? "text-slate-400 cursor-not-allowed"
                                    : "text-slate-600 hover:text-slate-800"
                                }`}
                                onClick={() => !isEditLocked && patchLine(i, "showDiscount", false)}
                                disabled={isEditLocked}
                              >
                                Hide discount
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 h-11">
                            <button
                              type="button"
                              className={`px-3.5 py-2 rounded-lg border text-sm ${
                                isEditLocked ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-50"
                              }`}
                              onClick={() => !isEditLocked && patchLine(i, "showDiscount", true)}
                              disabled={isEditLocked}
                            >
                              Add discount
                            </button>
                            {hasDiscount && (
                              <span
                                className="text-[12px] px-2 py-1 rounded-full border bg-slate-50 text-slate-700"
                                title="Click to edit"
                                onClick={() => !isEditLocked && patchLine(i, "showDiscount", true)}
                                style={{
                                  cursor: isEditLocked ? "not-allowed" : "pointer",
                                  opacity: isEditLocked ? 0.6 : 1,
                                }}
                              >
                                −
                                {Number(li.discountAmount) > 0
                                  ? money(li.discountAmount, currency)
                                  : `${li.discountPercent}%`}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <LabeledInput
                        label="Tax %"
                        type="number"
                        value={li.taxPercent}
                        onChange={(e) => patchLine(i, "taxPercent", Number(e.target.value || 0))}
                        rightAdornment={<Percent size={14} />}
                        disabled={isEditLocked}
                      />
                      <div className="pt-7">
                        <LabeledCheckbox
                          label="Tax incl."
                          checked={!!li.isTaxInclusive}
                          onChange={(e) => patchLine(i, "isTaxInclusive", e.target.checked)}
                          disabled={isEditLocked}
                        />
                      </div>
                    </div>

                    {/* Description + totals */}
                    <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-3">
                      <div className="lg:col-span-6">
                        <label className={LABEL_BASE}>Description</label>
                        <textarea
                          rows={2}
                          className={INPUT_BASE + " h-auto"}
                          placeholder="Describe the product or service"
                          value={li.description}
                          onChange={(e) => patchLine(i, "description", e.target.value)}
                          disabled={isEditLocked}
                        />
                      </div>

                      <div className="lg:col-span-6 grid grid-cols-3 md:grid-cols-6 gap-3 text-[13px]">
                        <div className="col-span-1">
                          <div className="text-slate-500">Subtotal</div>
                          <div className="font-semibold tabular-nums">
                            {money(c.subtotal, currency)}
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="text-slate-500">Tax</div>
                          <div className="font-semibold tabular-nums">
                            {money(c.taxAmount, currency)}
                          </div>
                        </div>
                        <div className="col-span-1 md:col-span-2">
                          <div className="text-slate-500">Line Total</div>
                          <div className="font-semibold tabular-nums">
                            {money(c.total, currency)}
                          </div>
                        </div>
                        <div className="col-span-3 md:col-span-2 flex items-end justify-end">
                          <div className={HINT_BASE}>
                            Amount first, then % (if both set, amount wins).
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between items-center pt-1">
              <div className={HINT_BASE + " flex items-center gap-1"}>
                <Info size={14} />
                Use ↑/↓ to re-order. Totals update instantly.
              </div>
              <button
                type="button"
                className={`px-3.5 py-2.5 rounded-xl border text-sm ${
                  isEditLocked ? "opacity-60 cursor-not-allowed" : "hover:bg-slate-50"
                }`}
                onClick={addLine}
                disabled={isEditLocked}
              >
                <Plus className="inline -mt-1 mr-1" size={16} />
                Add another line
              </button>
            </div>
          </Section>

          {/* Notes */}
          <Section icon={FileText} title="Notes" hint="Shown on the quote PDF">
            <textarea
              rows={3}
              className={INPUT_BASE + " h-auto"}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional message / terms / thank you note"
              disabled={isEditLocked}
            />
          </Section>
        </div>

        {/* Right column: sticky summary */}
        <div className="space-y-6">
          <div className="sticky top-6">
            <Section icon={DollarSign} title="Summary" hint="Review totals before saving">
              <div className="space-y-2 text-[15px]">
                <div className="flex justify-between">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="font-semibold tabular-nums">
                    {money(totals.subtotalBase, currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Discounts</span>
                  <span className="font-semibold tabular-nums">
                    -{money(aggregatedDiscountDisplay, currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Tax</span>
                  <span className="font-semibold tabular-nums">
                    {money(totals.taxTotal, currency)}
                  </span>
                </div>
                <div className="h-px bg-slate-200 my-2" />
                <div className="flex justify-between text-lg">
                  <span className="font-semibold text-slate-800">Total</span>
                  <span className="font-extrabold tabular-nums">
                    {money(totals.totalAmount, currency)}
                  </span>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-2">
                <button
                  type="button"
                  disabled={!canSave || saving}
                  onClick={() => save({ closeAfter: false })}
                  className={`px-4 py-2 rounded-xl font-semibold text-white shadow-sm ${
                    !canSave || saving ? "bg-slate-400" : "bg-slate-900 hover:bg-black"
                  }`}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  disabled={!canSave || saving}
                  onClick={() => save({ closeAfter: true })}
                  className={`px-4 py-2 rounded-xl font-semibold border ${
                    !canSave || saving
                      ? "text-slate-400 border-slate-200"
                      : "text-slate-900 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  Save & Close
                </button>

                <div className="text-[12px] text-slate-500 flex items-center gap-2 mt-2">
                  <Info size={14} className="text-slate-400" />
                  Rounding: HALF_UP • Expiry must be ≥ issue date
                </div>
              </div>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}
